import {
    Fragment,
    cloneElement,
    isValidElement,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
    type ReactElement,
    type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Search, X } from 'lucide-react';
import {
    cx,
    splitStyleProps,
    toneToAlertClass,
    toneToBadgeClass,
    useBreakpoint,
    type SxObject,
} from './utils';
import { useSharedUiText } from './sharedUiText';
import { ChunkErrorBoundary } from '../components/ChunkErrorBoundary';
import { StaleAppPanel } from '../components/StaleAppPanel';

export function LoadingState({ message = '載入中...' }: { message?: string }) {
    return (
        <div className="flex min-h-48 items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-base-content/70">
                <span className="loading loading-spinner loading-lg text-primary" />
                <span className="text-sm">{message}</span>
            </div>
        </div>
    );
}

export function EmptyState({
    title,
    description,
    action,
}: {
    title: string;
    description?: string;
    action?: ReactNode;
}) {
    return (
        <div className="card border border-dashed border-base-300 bg-base-100">
            <div className="card-body items-center text-center">
                <h3 className="text-lg font-semibold">{title}</h3>
                {description ? (
                    <p className="max-w-xl text-sm text-base-content/70">{description}</p>
                ) : null}
                {action}
            </div>
        </div>
    );
}

export function ErrorState({ message }: { message: string }) {
    return <div className={cx('alert', toneToAlertClass('error'))}>{message}</div>;
}

export function SectionCard({
    title,
    description,
    actions,
    children,
    className,
}: {
    title?: ReactNode;
    description?: ReactNode;
    actions?: ReactNode;
    children: ReactNode;
    className?: string;
}) {
    return (
        <section className={cx('card bg-base-100 shadow-sm', className)}>
            <div className="card-body">
                {title || actions || description ? (
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div className="space-y-1">
                            {title ? <h2 className="text-lg font-semibold">{title}</h2> : null}
                            {description ? (
                                <div className="text-sm text-base-content/70">{description}</div>
                            ) : null}
                        </div>
                        {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
                    </div>
                ) : null}
                {children}
            </div>
        </section>
    );
}

export function StatCard({
    title,
    value,
    description,
    icon,
    tone = 'primary',
    onClick,
}: {
    title: ReactNode;
    value: ReactNode;
    description?: ReactNode;
    icon?: ReactNode;
    tone?: string;
    onClick?: () => void;
}) {
    const Component = onClick ? 'button' : 'div';

    return (
        <Component
            type={onClick ? 'button' : undefined}
            onClick={onClick}
            className={cx(
                'card bg-base-100 text-left shadow-sm transition',
                onClick
                    ? 'hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/30'
                    : ''
            )}
        >
            <div className="card-body">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="text-sm text-base-content/70">{title}</p>
                        <p className="text-4xl font-semibold">{value}</p>
                    </div>
                    {icon ? (
                        <div
                            className={cx(
                                'rounded-2xl p-3 text-white',
                                tone === 'warning'
                                    ? 'bg-warning'
                                    : tone === 'error'
                                      ? 'bg-error'
                                      : tone === 'success'
                                        ? 'bg-success'
                                        : 'bg-primary'
                            )}
                        >
                            {icon}
                        </div>
                    ) : null}
                </div>
                {description ? (
                    <div className="text-sm text-base-content/70">{description}</div>
                ) : null}
            </div>
        </Component>
    );
}

export function FilterBar({ children }: { children: ReactNode }) {
    return (
        <div className="card bg-base-100 shadow-sm">
            <div className="card-body">{children}</div>
        </div>
    );
}

export function DataTable({ children, className }: { children: ReactNode; className?: string }) {
    return (
        <div className={cx('data-table rounded-box bg-base-100 shadow-sm', className)}>
            {children}
        </div>
    );
}

export function StatusBadge({
    children,
    tone = 'default',
    outline = false,
}: {
    children: ReactNode;
    tone?: string;
    outline?: boolean;
}) {
    return (
        <span className={cx('badge gap-1', toneToBadgeClass(tone), outline ? 'badge-outline' : '')}>
            {children}
        </span>
    );
}

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** 開啟時把焦點移進對話框，關閉時還給原本的元素；Esc 關閉；Tab 只在對話框內循環 */
function useModalFocus(onClose?: () => void) {
    const surfaceRef = useRef<HTMLDivElement | null>(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const surface = surfaceRef.current;
        if (surface && !surface.contains(document.activeElement)) {
            surface.focus();
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && onCloseRef.current) {
                event.stopPropagation();
                onCloseRef.current();
                return;
            }
            if (event.key !== 'Tab' || !surface) return;
            const focusable = Array.from(surface.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            previouslyFocused?.focus?.();
        };
    }, []);

    return surfaceRef;
}

function ModalSurface({
    title,
    header,
    titleId,
    children,
    actions,
    onClose,
    maxWidth,
}: {
    title?: ReactNode;
    header?: ReactNode;
    titleId?: string;
    children: ReactNode;
    actions?: ReactNode;
    onClose?: () => void;
    maxWidth: string;
}) {
    const surfaceRef = useModalFocus(onClose);
    const t = useSharedUiText();
    const fallbackTitleId = useId();
    const labelledBy = header || title ? (titleId ?? fallbackTitleId) : undefined;

    return (
        <div className="app-modal fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-4">
            <div
                ref={surfaceRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={labelledBy}
                tabIndex={-1}
                className={cx(
                    'card max-h-[90dvh] w-full overflow-hidden bg-base-100 shadow-xl outline-none',
                    maxWidth
                )}
            >
                <div className="flex shrink-0 items-start justify-between gap-4 px-6 pt-6 pb-3">
                    {header ? (
                        <div className="min-w-0 flex-1">{header}</div>
                    ) : title ? (
                        <h3 id={labelledBy} className="text-xl font-semibold">
                            {title}
                        </h3>
                    ) : (
                        <span />
                    )}
                    {onClose ? (
                        <button
                            type="button"
                            className="btn btn-ghost btn-circle btn-sm -mt-1 -mr-2 shrink-0"
                            aria-label={t('close')}
                            onClick={onClose}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    ) : null}
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-6 py-2">
                    <ChunkErrorBoundary fallback={<StaleAppPanel />}>{children}</ChunkErrorBoundary>
                </div>
                {actions ? (
                    <div className="modal-action mt-0 shrink-0 px-6 pt-3 pb-6">{actions}</div>
                ) : (
                    <div className="h-5 shrink-0" />
                )}
            </div>
        </div>
    );
}

export function AppModal({
    open,
    title,
    header,
    titleId,
    children,
    actions,
    onClose,
    maxWidth = 'max-w-2xl',
}: {
    open: boolean;
    title?: ReactNode;
    /** 取代預設標題列的內容（Dialog 把 DialogTitle 放在這裡）；請自行提供 titleId 供 aria-labelledby 使用 */
    header?: ReactNode;
    titleId?: string;
    children: ReactNode;
    actions?: ReactNode;
    onClose?: () => void;
    maxWidth?: string;
}) {
    if (!open) return null;

    return createPortal(
        <ModalSurface
            title={title}
            header={header}
            titleId={titleId}
            actions={actions}
            onClose={onClose}
            maxWidth={maxWidth}
        >
            {children}
        </ModalSurface>,
        document.body
    );
}

export function ConfirmModal({
    open,
    title,
    message,
    confirmText = '確認',
    cancelText = '取消',
    confirmTone = 'primary',
    onConfirm,
    onCancel,
}: {
    open: boolean;
    title: string;
    message: ReactNode;
    confirmText?: string;
    cancelText?: string;
    confirmTone?: string;
    onConfirm: () => void;
    onCancel: () => void;
}) {
    return (
        <AppModal
            open={open}
            title={title}
            onClose={onCancel}
            maxWidth="max-w-md"
            actions={
                <>
                    <button type="button" className="btn btn-ghost" onClick={onCancel}>
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        className={cx(
                            'btn',
                            confirmTone === 'error'
                                ? 'btn-error'
                                : confirmTone === 'warning'
                                  ? 'btn-warning'
                                  : confirmTone === 'success'
                                    ? 'btn-success'
                                    : confirmTone === 'info'
                                      ? 'btn-info'
                                      : 'btn-primary'
                        )}
                        onClick={onConfirm}
                    >
                        {confirmText}
                    </button>
                </>
            }
        >
            <p className="text-sm leading-6 text-base-content/80">{message}</p>
        </AppModal>
    );
}

interface SearchSelectOption<T> {
    value: T;
    label: string;
}

export function SearchSelect<T>({
    options,
    value,
    multiple = false,
    placeholder,
    disabled,
    onChange,
    className,
}: {
    options: Array<SearchSelectOption<T>>;
    value: T | T[] | null;
    multiple?: boolean;
    placeholder?: string;
    disabled?: boolean;
    onChange: (value: T | T[] | null) => void;
    className?: string;
}) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const selected = useMemo(() => {
        if (multiple) {
            return Array.isArray(value) ? value : [];
        }
        return value ?? null;
    }, [multiple, value]);

    const selectedValues = useMemo(() => {
        const selectedArray = Array.isArray(selected) ? selected : selected ? [selected] : [];
        return options.filter((option) => selectedArray.includes(option.value));
    }, [options, selected]);

    const filtered = useMemo(() => {
        if (!query) return options;
        const lowered = query.toLowerCase();
        return options.filter((option) => option.label.toLowerCase().includes(lowered));
    }, [options, query]);

    useEffect(() => {
        function handleClick(event: MouseEvent) {
            if (!containerRef.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        }

        window.addEventListener('click', handleClick);
        return () => window.removeEventListener('click', handleClick);
    }, []);

    const handleSelect = (option: SearchSelectOption<T>) => {
        if (multiple) {
            const current = Array.isArray(value) ? value : [];
            if (current.includes(option.value)) {
                onChange(current.filter((item) => item !== option.value));
            } else {
                onChange([...current, option.value]);
            }
        } else {
            onChange(option.value);
            setOpen(false);
            setQuery(option.label);
        }
    };

    const removeValue = (item: SearchSelectOption<T>) => {
        if (!multiple || !Array.isArray(value)) return;
        onChange(value.filter((candidate) => candidate !== item.value));
    };

    return (
        <div ref={containerRef} className={cx('relative', className)}>
            <label className="input flex items-center gap-2">
                <Search className="h-4 w-4 text-base-content/50" />
                <div className="flex flex-1 flex-wrap items-center gap-2">
                    {multiple && selectedValues.length > 0
                        ? selectedValues.map((item) => (
                              <span key={item.label} className="badge badge-neutral gap-1">
                                  {item.label}
                                  <button type="button" onClick={() => removeValue(item)}>
                                      <X className="h-3 w-3" />
                                  </button>
                              </span>
                          ))
                        : null}
                    <input
                        type="text"
                        className="grow bg-transparent outline-none"
                        value={query}
                        placeholder={placeholder}
                        disabled={disabled}
                        onFocus={() => setOpen(true)}
                        onChange={(event) => {
                            setQuery(event.target.value);
                            setOpen(true);
                        }}
                    />
                </div>
            </label>
            {open ? (
                <div className="absolute z-30 mt-2 max-h-64 w-full overflow-auto rounded-2xl border border-base-300 bg-base-100 p-2 shadow-xl">
                    {filtered.length ? (
                        filtered.map((option) => {
                            const active = Array.isArray(value)
                                ? value.includes(option.value)
                                : value === option.value;

                            return (
                                <button
                                    key={option.label}
                                    type="button"
                                    className={cx(
                                        'btn btn-ghost btn-sm mb-1 flex w-full justify-start',
                                        active ? 'bg-primary/10 text-primary' : ''
                                    )}
                                    onClick={() => handleSelect(option)}
                                >
                                    {option.label}
                                </button>
                            );
                        })
                    ) : (
                        <div className="px-3 py-2 text-sm text-base-content/60">沒有符合的選項</div>
                    )}
                </div>
            ) : null}
        </div>
    );
}

export {
    DatePicker as DateField,
    TimePicker as TimeField,
    DateTimePicker as DateTimeField,
} from './DateTimePicker';

export function AppShell({
    sidebar,
    header,
    children,
}: {
    sidebar: ReactNode;
    header: ReactNode;
    children: ReactNode;
}) {
    return (
        <div className="min-h-screen bg-base-200">
            <div className="drawer lg:drawer-open">
                <input id="app-shell-drawer" type="checkbox" className="drawer-toggle" />
                <div className="drawer-content min-h-screen">
                    {header}
                    <main className="mx-auto w-full max-w-[1600px] p-5 md:p-8 xl:p-10">
                        {children}
                    </main>
                </div>
                <div className="drawer-side z-40">
                    <label htmlFor="app-shell-drawer" className="drawer-overlay" />
                    {sidebar}
                </div>
            </div>
        </div>
    );
}

export function RenderWithProps({
    element,
    extraProps,
}: {
    element: ReactElement | ReactNode;
    extraProps: Record<string, unknown>;
}) {
    if (isValidElement(element)) {
        return cloneElement(element, extraProps);
    }

    return <Fragment>{element}</Fragment>;
}

export function Portal({ children }: { children: ReactNode }) {
    const id = useId();
    const [target, setTarget] = useState<HTMLElement | null>(null);

    useEffect(() => {
        const node = document.createElement('div');
        node.dataset.portalId = id;
        document.body.appendChild(node);
        setTarget(node);
        return () => {
            node.remove();
        };
    }, [id]);

    if (!target) return null;
    return createPortal(children, target);
}

export function InlineStyleBox({
    component: Component = 'div',
    children,
    sxProp,
    className,
    style: styleProp,
    ...rest
}: {
    component?: any;
    children?: ReactNode;
    sxProp?: Record<string, unknown>;
    className?: string;
    style?: React.CSSProperties;
    [key: string]: unknown;
}) {
    useBreakpoint();
    const { domProps, style } = splitStyleProps(rest, sxProp as SxObject | undefined);
    return (
        <Component className={className} style={{ ...style, ...styleProp }} {...domProps}>
            {children}
        </Component>
    );
}

export function toSelectOptions<T>(
    options: T[],
    getOptionLabel: (option: T) => string
): Array<SearchSelectOption<T>> {
    return options.map((option) => ({ value: option, label: getOptionLabel(option) }));
}

export function castNativeValue(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    if (event.target.type === 'number') {
        return event.target.value === '' ? '' : Number(event.target.value);
    }

    if (
        (event.target as HTMLInputElement).checked !== undefined &&
        event.target.type === 'checkbox'
    ) {
        return (event.target as HTMLInputElement).checked;
    }

    return event.target.value;
}
